import React, { useMemo, useState } from 'react';
import { Grid, TextField, Typography, Button, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  PageHeader,
  SearchFilterBar,
  DataTable,
  StatusChip,
  FormDialog,
  ConfirmDialog,
  flattenPages,
} from '../components';
import {
  useVendorsPaginated,
  useCreateVendor,
  useUpdateVendor,
  useDeactivateVendor,
  useActivateVendor,
  useDeleteVendor,
} from '../hooks/useVendors';
import { useBusinessUuid } from '../useBusinessUuid';
import { colors, SHOW_CURRENCY_FIELD } from '../constants';
import AddressAutocompleteInput from '../components/AddressAutocompleteInput';

const emptyForm = {
  name: '', code: '', type: 'supplier', contactName: '', email: '', phone: '',
  paymentTerms: 'net_30', currency: 'USD', taxId: '', leadTimeDays: '0',
  minimumOrderAmount: '0', street: '', city: '', state: '', postalCode: '', country: '',
};

// Vendor code is required + unique per business on the backend, but it is no
// longer entered/shown in the UI. Auto-generate a compact unique code on create.
const generateVendorCode = () => `VND-${Date.now().toString(36).toUpperCase()}`;

// Format a phone value as a US number while typing: (123) 456-7890.
// Non-digits are ignored and the mask is applied progressively, so the field
// always displays a clean, consistently formatted number.
const formatPhone = (value) => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Phone is optional, but when entered it must be a complete 10-digit US number.
// Returns an error message string, or null when valid.
const validateVendorPhone = (value) => {
  if (!value || !value.trim()) return null;
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 10) return 'Enter a valid 10-digit phone number.';
  return null;
};

function toForm(v) {
  return {
    name: v.name ?? '', code: v.code ?? '', type: v.type ?? 'supplier',
    contactName: v.contactName ?? '', email: v.email ?? '', phone: formatPhone(v.phone ?? ''),
    paymentTerms: v.paymentTerms ?? 'net_30', currency: v.currency ?? 'USD',
    taxId: v.taxId ?? '', leadTimeDays: String(v.leadTimeDays ?? 0),
    minimumOrderAmount: String(v.minimumOrderAmount ?? 0),
    street: v.address?.street ?? '', city: v.address?.city ?? '',
    state: v.address?.state ?? '', postalCode: v.address?.postalCode ?? '',
    country: v.address?.country ?? '',
  };
}

export default function Vendors() {
  const businessUuid = useBusinessUuid();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState(null);
  const [nameError, setNameError] = useState(null);
  const [phoneError, setPhoneError] = useState(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [confirmActivate, setConfirmActivate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const params = useMemo(() => ({ limit: 20, search: search || undefined }), [search]);
  const query = useVendorsPaginated(params);
  const createVendor = useCreateVendor();
  const updateVendor = useUpdateVendor();
  const deactivateVendor = useDeactivateVendor();
  const activateVendor = useActivateVendor();
  const deleteVendor = useDeleteVendor();

  const vendors = flattenPages(query);
  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // Fill the whole address block from a selected Google Places suggestion.
  // Individual fields remain editable afterwards.
  const handleAddressSelected = (data) => {
    setForm((f) => ({
      ...f,
      street: data?.address1 || '',
      city: data?.city || '',
      state: data?.state || '',
      postalCode: data?.zipCode || '',
      country: data?.country || '',
    }));
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setError(null);
    setNameError(null);
    setPhoneError(null);
    setOpen(true);
  };

  const openEdit = (vendor) => {
    setEditing(vendor);
    setForm(toForm(vendor));
    setError(null);
    setNameError(null);
    setPhoneError(validateVendorPhone(vendor.phone ?? ''));
    setOpen(true);
  };

  const handleSubmit = async () => {
    setError(null);
    if (!form.name.trim()) {
      setNameError('Name is required.');
      return;
    }
    const phoneValidationError = validateVendorPhone(form.phone);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      return;
    }
    // Code is hidden: auto-generated on create, preserved as-is on edit.
    const code = editing ? form.code : generateVendorCode();
    // businessUuid is sent as a query param (not in the body). The update DTO
    // rejects unknown body properties, so keep it out of the payload here.
    const payload = {
      code,
      name: form.name,
      type: form.type,
      contactName: form.contactName,
      email: form.email,
      phone: form.phone,
      paymentTerms: form.paymentTerms,
      currency: form.currency,
      taxId: form.taxId,
      leadTimeDays: parseInt(form.leadTimeDays, 10) || 0,
      minimumOrderAmount: parseFloat(form.minimumOrderAmount) || 0,
      address: {
        street: form.street,
        city: form.city,
        state: form.state,
        postalCode: form.postalCode,
        country: form.country,
      },
    };
    try {
      if (editing) {
        await updateVendor.mutateAsync({ uuid: editing.uuid, data: payload });
      } else {
        await createVendor.mutateAsync({ businessUuid, ...payload });
      }
      setOpen(false);
    } catch (e) {
      const msg = e?.response?.data?.message;
      setError(
        (Array.isArray(msg) ? msg.join(', ') : msg) ||
          e.message ||
          'Something went wrong'
      );
    }
  };

  const handleDeactivate = async () => {
    try {
      await deactivateVendor.mutateAsync(editing.uuid);
      setConfirmDeactivate(false);
      setOpen(false);
    } catch (e) {
      setConfirmDeactivate(false);
      setError(e?.response?.data?.message || 'Deactivate failed');
    }
  };

  const handleActivate = async () => {
    try {
      await activateVendor.mutateAsync(editing.uuid);
      setConfirmActivate(false);
      setOpen(false);
    } catch (e) {
      setConfirmActivate(false);
      setError(e?.response?.data?.message || 'Activate failed');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteVendor.mutateAsync(editing.uuid);
      setConfirmDelete(false);
      setOpen(false);
    } catch (e) {
      setConfirmDelete(false);
      setError(e?.response?.data?.message || 'Delete failed');
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Vendor',
      render: (row) => (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600, color: colors.navy }}>
            {row.name}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.gray500 }}>
            {row.code}
          </Typography>
        </>
      ),
    },
    ...(SHOW_CURRENCY_FIELD ? [{ key: 'currency', label: 'Currency' }] : []),
    {
      key: 'isActive',
      label: 'Status',
      render: (row) => <StatusChip status={row.isActive === false ? 'inactive' : 'active'} />,
    },
  ];

  const saving = createVendor.isPending || updateVendor.isPending;

  return (
    <>
      <PageHeader
        title="Vendors"
        actionLabel="New Vendor"
        actionIcon={<AddIcon />}
        onAction={openCreate}
      />

      <SearchFilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search vendors..."
      />

      <DataTable
        columns={columns}
        rows={vendors}
        loading={query.isLoading}
        onRowClick={openEdit}
        emptyTitle="No vendors found"
        emptyMessage="Add your first vendor."
        hasNextPage={query.hasNextPage}
        isFetchingNextPage={query.isFetchingNextPage}
        fetchNextPage={query.fetchNextPage}
      />

      <FormDialog
        open={open}
        title={editing ? 'Edit Vendor' : 'New Vendor'}
        onClose={() => setOpen(false)}
        onSubmit={handleSubmit}
        submitLabel={editing ? 'Update Vendor' : 'Create Vendor'}
        loading={saving}
        maxWidth="md"
      >
        {error && (
          <Typography variant="body2" sx={{ color: colors.error, mb: 1.5 }}>
            {error}
          </Typography>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({ ...f, name: v }));
                setNameError(v.trim() ? null : 'Name is required.');
              }}
              error={Boolean(nameError)}
              helperText={nameError || ''}
              fullWidth
              required
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Contact Name" value={form.contactName} onChange={setField('contactName')} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Email" value={form.email} onChange={setField('email')} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone"
              value={form.phone}
              onChange={(e) => {
                const v = formatPhone(e.target.value);
                setForm((f) => ({ ...f, phone: v }));
                setPhoneError(validateVendorPhone(v));
              }}
              placeholder="(123) 456-7890"
              error={Boolean(phoneError)}
              helperText={phoneError || ''}
              fullWidth
              size="small"
            />
          </Grid>
          {SHOW_CURRENCY_FIELD && (
            <Grid item xs={6} sm={3}>
              <TextField label="Currency" value={form.currency} onChange={setField('currency')} fullWidth size="small" />
            </Grid>
          )}
          <Grid item xs={6} sm={3}>
            <TextField label="Tax ID" value={form.taxId} onChange={setField('taxId')} fullWidth size="small" />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }}>
              <Typography variant="caption" sx={{ color: colors.gray500 }}>ADDRESS</Typography>
            </Divider>
          </Grid>
          <Grid item xs={12} sm={6}>
            <AddressAutocompleteInput
              label="Street"
              value={form.street}
              onChange={(val) => setForm((f) => ({ ...f, street: val }))}
              onPlaceSelected={handleAddressSelected}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="City" value={form.city} onChange={setField('city')} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="State" value={form.state} onChange={setField('state')} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Postal Code" value={form.postalCode} onChange={setField('postalCode')} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Country" value={form.country} onChange={setField('country')} fullWidth size="small" />
          </Grid>

          {editing && (
            <Grid item xs={12} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {editing.isActive !== false ? (
                <Button
                  color="error"
                  onClick={() => setConfirmDeactivate(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Deactivate vendor
                </Button>
              ) : (
                <Button
                  color="success"
                  onClick={() => setConfirmActivate(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Reactivate vendor
                </Button>
              )}
              <Button
                color="error"
                variant="outlined"
                onClick={() => setConfirmDelete(true)}
                sx={{ textTransform: 'none' }}
              >
                Delete vendor
              </Button>
            </Grid>
          )}
        </Grid>
      </FormDialog>

      <ConfirmDialog
        open={confirmDeactivate}
        title="Deactivate Vendor"
        message={editing ? `Deactivate "${editing.name}"?` : ''}
        confirmLabel="Deactivate"
        destructive
        loading={deactivateVendor.isPending}
        onConfirm={handleDeactivate}
        onClose={() => setConfirmDeactivate(false)}
      />

      <ConfirmDialog
        open={confirmActivate}
        title="Reactivate Vendor"
        message={editing ? `Reactivate "${editing.name}"?` : ''}
        confirmLabel="Reactivate"
        loading={activateVendor.isPending}
        onConfirm={handleActivate}
        onClose={() => setConfirmActivate(false)}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete Vendor"
        message={
          editing
            ? `Permanently delete "${editing.name}"? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        destructive
        loading={deleteVendor.isPending}
        onConfirm={handleDelete}
        onClose={() => setConfirmDelete(false)}
      />
    </>
  );
}
